import axios from 'axios';
import { settings, TOKEN } from 'utils';

export type UploadDocumentResponse = {
  id: number;
  tag: string;
  url: string;
  entity_id: number | null;
};

export type ValidateDocumentsPayload = {
  document_ids: number[];
  data?: {
    tax_id?: string;
    expected_year?: string;
    expected_start_year?: string;
    expected_end_year?: string;
  };
};

export type ValidateDocumentsResponse = {
  message: string;
  batch_id: number;
};

// BE's per-document financials coverage check — sent as the digitization
// status poll's `data` object (financials only; other doc types still send
// `data` as a flat array). Each flag is true once an accepted document of
// that type has been found across the whole batch, independent of any
// single file's own status.
export type FinancialsCoveredFlags = {
  ytd_pnl_found: boolean;
  ytd_balance_sheet_found: boolean;
  pnl_found: boolean;
  balance_sheet_found: boolean;
  previous_yr_pnl_found: boolean;
  previous_yr_bs_found: boolean;
};

export type FinancialsDigitizationData = {
  documents: Array<{
    document_id: number;
    document_type: string | null;
    status?: string | null;
  }>;
  covered_documents: FinancialsCoveredFlags;
};

export type DigitizationStatusResponse = {
  batch_id: number;
  document_ids: number[];
  status: string;
  rejection_reasons?: string[];
  data?: any;
};

export type RemoveDocumentsPayload = {
  document_ids: number[];
};

export type OwnerPayload = {
  full_name: string;
  title: string;
  ownership_percentage: number;
  email: string;
  phone_code: string;
  phone_number: string;
};

export type SaveOwnersPayload = {
  owners: OwnerPayload[];
};

export type LegalSignatoryPayload = {
  legal_signatory: {
    name: string;
    title: string;
    email: string;
    is_owner: boolean;
    phone: { country_code: string; phone_number: string };
  };
};

export const putLegalSignatory = async (
  businessId: string,
  customerId: string,
  payload: LegalSignatoryPayload
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/legal_signatory`,
    method: 'PUT',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: payload,
  });
  return res.data;
};

export const postSaveOwners = async (
  leadId: string,
  body: SaveOwnersPayload
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/leads/${leadId}/owners`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: body,
  });
  return res.data;
};

export const postUploadDocument = async (
  leadId: string,
  file: File,
  tag: string,
  source: string,
  onProgress?: (percent: number) => void
): Promise<UploadDocumentResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/leads/${leadId}/documents?tag=${tag}&source=${source}`,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: formData,
    onUploadProgress: onProgress
      ? (evt) => {
          if (evt.total) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        }
      : undefined,
  });
  return res.data as UploadDocumentResponse;
};

export const postRemoveDocuments = async (
  leadId: string,
  body: RemoveDocumentsPayload
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/leads/${leadId}/documents/remove`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: body,
  });
  return res.data;
};

export const postValidateDocuments = async (
  leadId: string,
  body: ValidateDocumentsPayload
): Promise<ValidateDocumentsResponse> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/leads/${leadId}/documents/validate`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: body,
  });
  return res.data as ValidateDocumentsResponse;
};

export const getDigitizationStatus = async (
  leadId: string,
  batchId: number
): Promise<DigitizationStatusResponse> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/leads/${leadId}/documents/${batchId}/digitization_status`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
  return res.data as DigitizationStatusResponse;
};

export type AppDocFile = {
  url: string;
  name: string;
  document_id: number;
  tags: string;
  digitization: {
    status?: string;
    batch_id?: number;
    rejection_reasons?: string[] | null;
    document_type?: string | null;
  };
};

// Note saved against a failed document.
export type CustomerNote = {
  note: string;
  author_type?: string | null;
  updated_at?: string | null;
};

export type AppV2Application = {
  id: number;
  created_at: string;
  onboarding_stage: string;
  customer_notes?: Record<string, CustomerNote>;
  uid: string;
  last_modified_details: {
    last_modified_by: 'drip' | 'channel_partner' | 'customer';
    last_modified_by_name: string;
    last_modified_at: string;
    updated_by_bdm: boolean;
  } | null;
  mail_sent?: boolean;
  mail_sent_date?: string | null;
  // 'sdk' when the logged-in POC is themself the majority owner (they verify
  // identity in-app here); 'email_link' otherwise — a separate majority
  // owner completes verification via an emailed link, so this app shows no
  // action for the current viewer to take.
  onfido_verification?: {
    verification_method: 'sdk' | 'email_link';
  };
  business_details: {
    id: number;
    company_name: string;
    ein: string | null;
    company_type: string | null;
    state_of_registration: string | null;
    website: string | null;
    dba: string | null;
    year_incorporated: string | null;
    location: {
      country: string | null;
      state: string | null;
      city: string | null;
      zip_code: string | null;
      address: string | null;
    };
    physical_operations_address: {
      city: string | null;
      state: string | null;
      address: string | null;
      country: string | null;
      zip_code: string | null;
      same_as_location: boolean;
    } | null;
    ecom_seller: { is_ecom_seller: string; ecom_seller_link: string } | null;
    scf_owners: Array<{
      name: string;
      email?: string;
      phone?: { country_code: string; phone_number: string } | null;
      user_id?: string;
      ownership: string;
      title?: string;
      owner_type?: string;
      personal_gurantee?: string;
      unicourt_monitoring_enabled?: boolean;
      // Where this owner's data came from — 'tax_returns' means it was
      // cleanly extracted with no mismatch (cap table not needed for this
      // owner); 'ownership-document' means a real cap table was uploaded;
      // 'customer' means the applicant typed it in manually.
      data_source?: 'tax_returns' | 'ownership-document' | 'customer';
    }> | null;
    details_pending: boolean;
  };
  personal_details: {
    onfido_upload_completed: boolean;
    details_pending: boolean;
  };
  business_verification: {
    relationship_type?: string | null;
    tax_return_data: any | null;
    parent_company_details?: any | null;
    sister_company_details?: any | null;
    latest_revenue: string | number | null;
  } | null;
  tax_returns: { status: string; files: AppDocFile[] } | null;
  bank_details: {
    plaid_linked: boolean;
    plaid_failed: boolean;
    files: AppDocFile[];
    status: string;
    additional_files: AppDocFile[];
    accounts: Array<{
      account_number_suffix: string;
      document_ids: number[];
      covered_months: (number | string)[];
      missing_months: (number | string)[];
    }>;
  };
  financials: {
    status: string;
    files: AppDocFile[];
    covered_files?: FinancialsCoveredFlags;
  } | null;
  certificate_of_incorporation: { status: string; files: AppDocFile[] } | null;
  provisional_financials: { status: string; files: AppDocFile[] } | null;
  balance_sheet: { status: string; files: AppDocFile[] } | null;
  pl_statement: { status: string; files: AppDocFile[] } | null;
  ageing_reports: { status: string; files: AppDocFile[] } | null;
  debt_profile: { status: string; files: AppDocFile[] } | null;
  dba_documents: { status: string; files: AppDocFile[] };
  ownership_documents: { status: string; files: AppDocFile[] };
  legal_signatory: {
    name: string;
    email: string;
    phone: { country_code: string; phone_number: string } | null;
    title: string;
    is_owner: boolean;
  } | null;
};

// Notes are keyed by document tag → reason, e.g. { 'financials': 'reason', ... }.
// The BE iterates this as a hash (customer_notes.each { |tag, note| ... }).
export type UpdateStatusNotes = Record<string, string>;

// Saves a note as soon as it is entered, without submitting.
export const postCustomerNote = async (
  businessId: string,
  customerId: string,
  notes: UpdateStatusNotes
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/application/customer_notes`,
    method: 'POST',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: { notes },
  });
  return res.data;
};

export const postUpdateStatus = async (
  businessId: string,
  customerId: string,
  notes: UpdateStatusNotes = {}
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/application/update_status`,
    method: 'POST',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: { notes },
  });
  return res.data;
};

export type PostBusinessVerificationPayload = {
  business_verification: {
    relationship_type?: string;
    applicant?: {
      company?: string;
      ein?: string;
      company_type?: string;
      state_of_registration?: string;
      dba?: string;
    };
  };
};

export const postBusinessVerification = async (
  businessId: string,
  customerId: string,
  payload: PostBusinessVerificationPayload
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}/application/business_verification`,
    method: 'POST',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: payload,
  });
  return res.data;
};

export type PatchScfImporterPayload = {
  scf_importer: {
    company?: string;
    ein?: string;
    company_type?: string;
    state_of_registration?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zip_code?: string;
    dba?: string | null;
    website?: string;
    physical_operations_address?: {
      same_as_location: string;
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      zip_code?: string;
    };
    ecom_seller?: {
      is_ecom_seller: string;
      ecom_seller_link?: string;
    };
  };
};

export const patchScfImporter = async (
  businessId: string,
  customerId: string,
  payload: PatchScfImporterPayload
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/importers/${businessId}`,
    method: 'PATCH',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: payload,
  });
  return res.data;
};

export type ScfOwnerPayload = {
  user_id?: string;
  name: string;
  title: string;
  email: string;
  ownership: number;
  phone: { country_code: string; phone_number: string };
  data_source?: 'tax_returns' | 'ownership-document' | 'customer';
};

export type PatchScfUserPayload = {
  user: {
    name?: string;
    title?: string;
    email?: string;
    ownership?: number;
    phone?: { country_code: string; phone_number: string };
    business_id?: number;
    owners?: ScfOwnerPayload[];
  };
};

export const patchScfUser = async (
  userId: string,
  payload: PatchScfUserPayload
): Promise<any> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v1/onboarding/scf/users/${userId}`,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
    data: payload,
  });
  return res.data;
};

export const getApplicationV2Details = async (
  businessId: string,
  customerId: string
): Promise<{ message: string; application: AppV2Application }> => {
  const res = await axios({
    url: `${process.env.REACT_APP_REST_API_URL}/v2/onboarding/scf/importers/${businessId}/application/details`,
    method: 'GET',
    params: { customer_id: customerId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.getCookie(TOKEN)}`,
    },
  });
  return res.data;
};
